"use client";

import { useState, useEffect } from "react";
import { ShieldCheck, Lock, FileCheck, Globe, HeartPulse, CreditCard, Star, ClipboardCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Compliance() {
  const [isZh, setIsZh] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsZh(localStorage.getItem("elite-language") === "zh-Hant");
    }
  }, []);
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const certificationsEn = [{
    name: "BV Certified",
    meta: "Bureau Veritas Inspection",
    Icon: ShieldCheck
  }, {
    name: "ITS Verified",
    meta: "Intertek Quality Assurance",
    Icon: Lock
  }, {
    name: "SGS Audited",
    meta: "Global Testing Standards",
    Icon: FileCheck
  }, {
    name: "TÜV Rheinland",
    meta: "Safety & Performance",
    Icon: Globe
  }, {
    name: "CCC Mark",
    meta: "China Compulsory Certification",
    Icon: HeartPulse
  }, {
    name: "G-Mark",
    meta: "Gulf States Compliance",
    Icon: CreditCard
  }, {
    name: "ISO 9001",
    meta: "Quality Management System",
    Icon: Star
  }, {
    name: "ASTM F963",
    meta: "Toy Safety Standards",
    Icon: ClipboardCheck
  }];
  const certificationsZh = [{
    name: "BV 認證",
    meta: "必維國際檢驗局檢測",
    Icon: ShieldCheck
  }, {
    name: "ITS 認證",
    meta: "天祥集團品質保證",
    Icon: Lock
  }, {
    name: "SGS 審核",
    meta: "全球檢測標準",
    Icon: FileCheck
  }, {
    name: "TÜV 萊茵",
    meta: "安全與性能評估",
    Icon: Globe
  }, {
    name: "CCC 認證",
    meta: "中國強制性產品認證",
    Icon: HeartPulse
  }, {
    name: "G-Mark",
    meta: "海灣國家合規標準",
    Icon: CreditCard
  }, {
    name: "ISO 9001",
    meta: "品質管理體系",
    Icon: Star
  }, {
    name: "ASTM F963",
    meta: "玩具安全標準",
    Icon: ClipboardCheck
  }];
  const certifications = isZh ? certificationsZh : certificationsEn;
  return <section data-section-id="20255" id="safety-compliance-standards" className="bg-background text-foreground">
      <div className="bg-background py-20 md:py-28 px-5 sm:px-8 lg:px-14">
        <div className="mx-auto max-w-[1240px]">
          <div ref={introRef} className="mx-auto max-w-[680px] text-center" style={{
          transform: introVisible ? "translateY(0)" : "translateY(16px)",
          opacity: introVisible ? 1 : 0,
          transition: "transform 640ms cubic-bezier(0.22, 1, 0.36, 1), opacity 640ms cubic-bezier(0.22, 1, 0.36, 1)"
        }}>
            <p className="font-default text-xs font-medium uppercase tracking-[0.15em] text-foreground">
              {isZh ? "全球品質保證" : "Global Quality Assurance"}
            </p>
            <h2 className="mt-5 font-default text-[clamp(2rem,3.6vw,3.25rem)] font-semibold leading-[1.2] tracking-[-0.02em] text-foreground">
              {isZh ? <>
                  以<span className="italic text-primary">嚴格</span>合規與認證締造卓越製造
                </> : <>
                  Manufacturing excellence through{" "}
                  <span className="italic text-primary">rigorous</span> compliance
                </>}
            </h2>
            <p className="mt-6 font-default text-[clamp(1rem,1.1vw,1.125rem)] leading-[1.6] text-foreground">
              {isZh ? "Elite Group Corporation Limited 確保每項產品均符合國際安全基準。我們與世界級實驗室合作，在各大市場全面保障您的品牌信譽與品質。" : "Elite Group Corporation Limited ensures every product meets international safety benchmarks. We partner with world-class labs to guarantee your brand's integrity in every market."}
            </p>
          </div>

          <div ref={gridRef} className="mt-14 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {certifications.map((cert, i) => {
            const Icon = cert.Icon;
            return <div key={cert.name} data-index={i} style={{
              transform: gridVisible ? "translateY(0)" : "translateY(16px)",
              opacity: gridVisible ? 1 : 0,
              transition: `transform 640ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 60}ms, opacity 640ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 60}ms`
            }}>
                  <Card className="group h-full bg-background border border-border px-6 py-8 flex flex-col items-center text-center transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-primary">
                    <div className="flex h-12 w-12 items-center justify-center text-foreground">
                      <Icon className="h-12 w-12" strokeWidth={1.5} aria-hidden="true" />
                    </div>

                    <div className="mt-6 h-px w-3/5 bg-border" aria-hidden="true" />

                    <h3 className="mt-6 font-default text-base font-semibold tracking-[-0.01em] text-foreground">
                      {cert.name}
                    </h3>
                    <p className="mt-2 font-default text-[0.85rem] font-normal tracking-[0.02em] text-muted-foreground">
                      {cert.meta}
                    </p>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}